﻿<script setup lang="ts">
    import { ref } from 'vue';
    import { type FontsReference } from '@shared/components/FontTypes';
    import { type TypeDesignSettings, uis } from '@shared/components/DesignSettings/defaultSettings';
    import DesignSettings from '@shared/components/DesignSettings/DesignSettings.vue';

    const { slider, colorPicker, toggle, fontSelector } = uis;

    const props = defineProps<{
        cssVars: { [key: string]: string };
        googleFonts: string;
    }>();

    let availableFonts = ref<FontsReference[]>([]);

    const emit = defineEmits(['update:cssVars' /*, 'update:theme'*/, 'update:googleFonts']);

    const designSettings: TypeDesignSettings = [
        {
            groupName: 'Frame',
            settings: [
                slider({ name: 'Width', options: { min: 400, max: 800, step: 10 } }),
                colorPicker({ name: 'BackgroundColor' }),
                $Resources.EditorPaddingHeading,
                slider({ name: 'PaddingTop', options: { max: 40, cols: 6 } }),
                slider({ name: 'PaddingRight', options: { max: 40, cols: 6 } }),
                slider({ name: 'PaddingBottom', options: { max: 40, cols: 6 } }),
                slider({ name: 'PaddingLeft', options: { max: 40, cols: 6 } }),
                $Resources.ThemeEditorBorderLabel,
                slider({ name: 'Radius', options: { max: 40, cols: 4 } }),
                slider({ name: 'BorderWidth', options: { max: 6, cols: 4 } }),
                colorPicker({ name: 'BorderColor', options: { cols: 4 } }),
            ],
        },
        {
            groupName: 'Image',
            settings: [
                toggle({
                    name: 'FlexDirection',
                    heading: $Resources.FormEditorDesignImageLayoutHeading,
                    toggleOptions: [
                        { value: 'column', label: $Resources.FormEditorDesignImageLayoutTop },
                        { value: 'row', label: $Resources.FormEditorDesignImageLayoutLeft },
                        { value: 'row-reverse', label: $Resources.FormEditorDesignImageLayoutRight },
                    ],
                }),
                slider({ name: 'Width', options: { min: 10, max: 60, step: 5, unit: '%' } }),
            ],
        },
        {
            groupName: 'Heading',
            settings: [
                fontSelector(),
                slider({ name: 'FontSize', options: { min: 12, max: 36, cols: 4 } }),
                slider({ name: 'LineHeight', options: { min: 90, max: 200, unit: '%', cols: 4 } }),
                colorPicker({ name: 'Color', options: { cols: 4 } }),
                toggle({
                    name: 'FontWeight',
                    heading: $Resources.FormEditorDesignFontWeightHeading,
                    toggleOptions: [
                        { value: '400', label: $Resources.FormEditorDesignFontWeightNormal },
                        { value: '700', label: $Resources.FormEditorDesignFontWeightBold },
                    ],
                }),
            ],
        },
        {
            groupName: 'Text',
            settings: [
                fontSelector(),
                slider({ name: 'FontSize', options: { min: 12, max: 36, cols: 4 } }),
                slider({ name: 'LineHeight', options: { min: 90, max: 200, unit: '%', cols: 4 } }),
                colorPicker({ name: 'Color', options: { cols: 4 } }),
                toggle({
                    name: 'FontWeight',
                    heading: $Resources.FormEditorDesignFontWeightHeading,
                    toggleOptions: [
                        { value: '400', label: $Resources.FormEditorDesignFontWeightNormal },
                        { value: '700', label: $Resources.FormEditorDesignFontWeightBold },
                    ],
                }),
            ],
        },
        {
            groupName: 'Labels',
            settings: [
                fontSelector(),
                slider({ name: 'FontSize', options: { min: 12, max: 36, cols: 4 } }),
                slider({ name: 'LineHeight', options: { min: 90, max: 200, unit: '%', cols: 4 } }),
                colorPicker({ name: 'Color', options: { cols: 4 } }),
                toggle({
                    name: 'FontWeight',
                    heading: $Resources.FormEditorDesignFontWeightHeading,
                    toggleOptions: [
                        { value: '400', label: $Resources.FormEditorDesignFontWeightNormal },
                        { value: '700', label: $Resources.FormEditorDesignFontWeightBold },
                    ],
                }),
            ],
        },
        {
            groupName: 'Inputs',
            settings: [
                colorPicker({ name: 'BackgroundColor' }),
                $Resources.EditorPaddingHeading,
                slider({ name: 'PaddingTop', options: { max: 40, cols: 6 } }),
                slider({ name: 'PaddingRight', options: { max: 40, cols: 6 } }),
                slider({ name: 'PaddingBottom', options: { max: 40, cols: 6 } }),
                slider({ name: 'PaddingLeft', options: { max: 40, cols: 6 } }),
                $Resources.ThemeEditorBorderLabel,
                slider({ name: 'BorderTopWidth', options: { max: 6, cols: 6 } }),
                slider({ name: 'BorderRightWidth', options: { max: 6, cols: 6 } }),
                slider({ name: 'BorderBottomWidth', options: { max: 6, cols: 6 } }),
                slider({ name: 'BorderLeftWidth', options: { max: 6, cols: 6 } }),
                slider({ name: 'Radius', options: { max: 40, cols: 6 } }),
                colorPicker({ name: 'BorderColor', options: { cols: 6 } }),
                $Resources.FormEditorDesignHeadingText,
                fontSelector(),
                slider({ name: 'FontSize', options: { min: 12, max: 36, cols: 4 } }),
                slider({ name: 'LineHeight', options: { min: 90, max: 200, unit: '%', cols: 4 } }),
                colorPicker({ name: 'Color', options: { cols: 4 } }),
            ],
        },
        {
            groupName: 'Button',
            settings: [
                colorPicker({ name: 'BackgroundColor', options: { cols: 6 } }),
                colorPicker({ name: 'BackgroundColorHover', options: { cols: 6 } }),
                $Resources.EditorPaddingHeading,
                slider({ name: 'PaddingTop', options: { max: 40, cols: 6 } }),
                slider({ name: 'PaddingRight', options: { max: 40, cols: 6 } }),
                slider({ name: 'PaddingBottom', options: { max: 40, cols: 6 } }),
                slider({ name: 'PaddingLeft', options: { max: 40, cols: 6 } }),
                $Resources.ThemeEditorBorderLabel,
                slider({ name: 'BorderWidth', options: { max: 6, cols: 4 } }),
                slider({ name: 'Radius', options: { max: 40, cols: 4 } }),
                colorPicker({ name: 'BorderColor', options: { cols: 4 } }),
                $Resources.FormEditorDesignHeadingText,
                fontSelector(),
                slider({ name: 'FontSize', options: { min: 12, max: 36, cols: 6 } }),
                slider({ name: 'LineHeight', options: { min: 90, max: 200, unit: '%', cols: 6 } }),
                colorPicker({ name: 'Color', options: { cols: 6 } }),
                colorPicker({ name: 'ColorHover', options: { cols: 6 } }),
                toggle({
                    name: 'FontWeight',
                    heading: $Resources.FormEditorDesignFontWeightHeading,
                    toggleOptions: [
                        { value: '400', label: $Resources.FormEditorDesignFontWeightNormal },
                        { value: '700', label: $Resources.FormEditorDesignFontWeightBold },
                    ],
                }),
                toggle({
                    name: 'JustifyContent',
                    heading: $Resources.FormEditorDesignAlignment,
                    toggleOptions: [
                        { value: 'start', label: $Resources.FormEditorDesignAlignmentLeft },
                        { value: 'center', label: $Resources.FormEditorDesignAlignmentCenter },
                        { value: 'end', label: $Resources.FormEditorDesignAlignmentRight },
                    ],
                }),
                toggle({
                    name: 'Width',
                    heading: $Resources.FormEditorDesignWidth,
                    toggleOptions: [
                        { value: 'auto', label: $Resources.FormEditorDesignWidthAuto },
                        { value: '100%', label: $Resources.FormEditorDesignWidthFull },
                    ],
                }),
            ],
        },
    ];
</script>

<script lang="ts"></script>

<template>
    <!--
	<h4>[Themes demo]</h4>
	<ul>
		<li v-for="theme in themes">
			<a href="#" @click="selectTheme(theme)">{{ theme.name }}</a>
		</li>
	</ul>
    -->

    <DesignSettings
        :cssVars="props.cssVars"
        :googleFonts="props.googleFonts"
        :designSettings="designSettings"
        :availableFonts="availableFonts"
        @update:cssVars="(name, newValue) => $emit('update:cssVars', name, newValue)"
        @update:googleFonts="(newValue) => $emit('update:googleFonts', newValue)"
        @update:availableFonts="(newValue) => (availableFonts = newValue)"></DesignSettings>
</template>

<style scoped></style>

<style lang="less"></style>
